/**
 * Booking Forms: Forms in a booking Integration Mode, which show a Booking
 * Page instead of fields (see CONTEXT.md). A Form is a booking Form whenever
 * its mode is (`isBookingMode(form.integrationMode)`), whether or not its
 * link is set, and never shows fields: one without a Booking Page shows
 * nothing. Pure, with no `env`, so the admin config, server actions and
 * client components can all import it.
 */

/**
 * The booking Integration Modes and the label each shows in the admin. The
 * booking options of the Forms `integrationMode` select are built from it.
 * A new booking mode starts with one entry here; the compiler then asks for
 * its link (`BOOKING_PAGE_LINKS`), its inline Booking Page
 * (`BOOKING_PAGE_EMBEDS` in `integration-form.tsx`) and how its button opens
 * it (`booking-button.tsx`).
 */
export const BOOKING_MODE_LABELS = { calendly: 'Calendly' } as const;

export type BookingMode = keyof typeof BOOKING_MODE_LABELS;

/** True when `mode` is a booking Integration Mode. */
export function isBookingMode(mode: unknown): mode is BookingMode {
  return typeof mode === 'string' && Object.prototype.hasOwnProperty.call(BOOKING_MODE_LABELS, mode);
}

export const BOOKING_MODES: readonly BookingMode[] = Object.keys(BOOKING_MODE_LABELS).filter(isBookingMode);

/**
 * The booking modes named for editors, e.g. "Calendly", or "Calendly or
 * HubSpot Meetings" once there are two. Every editor-facing string about
 * booking Forms as a whole uses it, so the copy follows the list; a string
 * about one mode's own behaviour (such as Calendly's popup) names that mode.
 */
export function bookingModeLabels(): string {
  return new Intl.ListFormat('en-GB', { type: 'disjunction' }).format(
    BOOKING_MODES.map((mode) => BOOKING_MODE_LABELS[mode]),
  );
}

/** Where a booking Form sends the visitor to book, and the mode that shows it. */
export type BookingPage = { mode: BookingMode; url: string };

/**
 * The Form fields that decide its Booking Page. Structural rather than
 * `Pick<Form, ...>`, so admin conditions and validators can pass the loosely
 * typed data they are given.
 */
type BookingFormFields = {
  integrationMode?: string | null;
  calendly?: { eventTypeUrl?: string | null } | null;
};

/** Where each booking mode stores its Booking Page's link. */
const BOOKING_PAGE_LINKS: Record<BookingMode, (form: BookingFormFields) => string | null | undefined> = {
  calendly: (form) => form.calendly?.eventTypeUrl,
};

/**
 * The Form's Booking Page, or `null` outside a booking mode or when its link
 * is empty.
 */
export function resolveBookingPage(form: BookingFormFields): BookingPage | null {
  const mode = form.integrationMode;
  if (!isBookingMode(mode)) return null;
  const url = BOOKING_PAGE_LINKS[mode](form)?.trim();
  return url ? { mode, url } : null;
}
